import classNames from 'classnames';
import * as React from 'react';
import type {
    PurchasableRestrictedSingleSubject,
    RestrictedSingleSubject,
} from '../../../Interfaces/RestrictedSingleSubject';
import { usePlannerContext } from '../../Planner/Context/PlannerContextProvider';
import { usePlannerTranslations } from '../../Planner/Context/PlannerTranslationsContextProvider';
import type { PlannerContentItem } from '../../Planner/Context/types';
import { SubjectsAggregator } from '../../SubjectsAggregator/SubjectsAggregator';
import { SubjectsContainer } from '../../SubjectsContainer/SubjectsContainer';
import { SummaryFocusManager } from '../../SummaryFocusManager/SummaryFocusManager';
import type { SubjectCategory } from '~/Features/StudyPage/Common/Interfaces/SubjectCategory';
import { creditsCalculator } from '~/Features/StudyPage/Online/Components/Utils/CreditsCalculator';
import { IsSubjectPurchasable } from '~/Features/StudyPage/Online/Components/Utils/TypeGuards/PurchasableRestrictedSingleSubjectTypeGuard';
import { FormattedText } from '~/Features/Txt/TxtBase';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { assertNever } from '~/Partials/Utils/typeUtils';
import styles from './PlannerContentElement.module.css';

export type PlannerContentElementProps = PlannerContentItem;

export const PlannerContentElement: React.FC<PlannerContentElementProps> = props => {
    const { container, elements } = props;
    const { obligatory, elective } = elements;

    const {
        currentBookmark,
        timeDetails,
        addBatchSelectedSubjects,
        addSelectedSubject,
        removeBatchSelectedSubjects,
        removeSelectedSubject,
        isSubjectDisabled,
        isSubjectSelected,
        setError,
    } = usePlannerContext();

    const { subjectCategory, description, creditsLabel, actions, semester } = usePlannerTranslations();
    const {
        electiveCreditsHeadingLabelFormat,
        specializationCreditsHeadingLabelFormat,
        obligatoryCreditsHeadingLabelFormat,
    } = description;
    const { addAllSubjects, addSubject, removeAllSubjects, removeSubject } = actions;

    const actionType = currentBookmark.contextKey === 'subjects' ? 'text' : 'icon';

    const renderFooter =
        currentBookmark.contextKey !== 'subjects' &&
        elective.size === 0 &&
        obligatory.subjects.find(subject => subject.canBePurchased) !== undefined;

    const currentSemester = container.key;
    const { obligatoryCreditsPerSemester, getTotalPointsPerElectiveGroupCategory } = creditsCalculator(
        obligatory.subjects,
        elective,
        currentBookmark.contextKey === 'fullTime'
            ? timeDetails.fullTimeSemesterCredits
            : timeDetails.partTimeSemesterCredits
    );

    const renderContainerHeading = () => {
        if (container.type === 'subjects') {
            const getSubjectContainerLabel = (category: SubjectCategory) => {
                switch (category) {
                    case 'obligatory':
                        return subjectCategory.obligatory;
                    case 'specialization':
                        return subjectCategory.specialization;
                    case 'elective':
                        return subjectCategory.elective;
                    default:
                        assertNever(category);
                }
            };

            return (
                <h3 className={classNames(styles.Heading, styles['Heading--subject'])}>
                    {getSubjectContainerLabel(container.key)}
                </h3>
            );
        }

        return (
            <h3 className={classNames(styles.Heading, styles['Heading--semester'])}>
                {`${currentSemester}. ${semester}`}
            </h3>
        );
    };

    const areAllEligibleSubjectsSelected = obligatory.subjects
        .filter(IsSubjectPurchasable)
        .every(subject => isSubjectSelected(subject, currentSemester));

    const handleBatchActionClick = () =>
        areAllEligibleSubjectsSelected
            ? removeBatchSelectedSubjects(currentSemester)
            : addBatchSelectedSubjects(obligatory.subjects.filter(IsSubjectPurchasable), currentSemester);

    const handleAddSelectedSubject = (subject: PurchasableRestrictedSingleSubject) =>
        isSubjectSelected(subject, currentSemester)
            ? removeSelectedSubject(subject)
            : addSelectedSubject(subject, currentSemester);

    const handleSubjectActionClick = (subject: RestrictedSingleSubject) =>
        IsSubjectPurchasable(subject)
            ? handleAddSelectedSubject(subject)
            : setError('addingNotPurchasableSubjectAttempt');

    const checkSubjectDisabledStatus = (subject: RestrictedSingleSubject) =>
        IsSubjectPurchasable(subject) ? isSubjectDisabled(subject, currentSemester) : false;

    const checkSubjectSelectionStatus = (subject: RestrictedSingleSubject) =>
        IsSubjectPurchasable(subject) ? isSubjectSelected(subject, currentSemester) : false;

    const getFooterActionLabel = () => {
        const hasMoreThanOneSubject = obligatory.subjects.length > 1;

        if (areAllEligibleSubjectsSelected) {
            return hasMoreThanOneSubject ? removeAllSubjects : removeSubject;
        }

        return hasMoreThanOneSubject ? addAllSubjects : addSubject;
    };

    const getFooterActionType = () => (areAllEligibleSubjectsSelected ? ButtonType.Outline : ButtonType.Solid);

    const renderContainerDescription = () => {
        if (container.type !== 'semesters') {
            return null;
        }

        const formatCreditsLabel = (text: string, availablePoints: number, renderPrefix: boolean) => {
            const electiveText = FormattedText(text, { credits: availablePoints, creditsName: creditsLabel });

            return renderPrefix ? `+ ${electiveText}` : electiveText;
        };

        const totalElectivePointsPerCategory = getTotalPointsPerElectiveGroupCategory();
        const totalElectivePoints = totalElectivePointsPerCategory?.get('elective') ?? 0;
        const totalSpecializationPoints = totalElectivePointsPerCategory?.get('specialization') ?? 0;

        const studyPointsDetails = [
            { label: obligatoryCreditsHeadingLabelFormat, points: obligatoryCreditsPerSemester },
            { label: electiveCreditsHeadingLabelFormat, points: totalElectivePoints },
            { label: specializationCreditsHeadingLabelFormat, points: totalSpecializationPoints },
        ].filter(details => details.points > 0);

        return (
            <div className={classNames(styles.Heading, styles['Heading--description'])}>
                {studyPointsDetails.map((details, index) => (
                    <span key={details.label}>{formatCreditsLabel(details.label, details.points, index > 0)}</span>
                ))}
            </div>
        );
    };

    const footerProps = renderFooter
        ? {
              action: {
                  onClick: handleBatchActionClick,
                  label: getFooterActionLabel(),
                  type: getFooterActionType(),
              } as const,
          }
        : undefined;

    return (
        <SubjectsContainer
            className={styles.PlannerContentElement}
            heading={{ label: renderContainerHeading(), description: renderContainerDescription() }}
            footer={footerProps}
            {...container}
        >
            <SubjectsAggregator
                {...elements}
                semester={currentSemester}
                onSubjectActionClick={handleSubjectActionClick}
                isSubjectSelected={checkSubjectSelectionStatus}
                isSubjectDisabled={checkSubjectDisabledStatus}
                subjectActionDisplayType={actionType}
            />
            <SummaryFocusManager />
        </SubjectsContainer>
    );
};
